import {
  createFileRoute,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { Center } from "@mantine/core";
import { useEffect, useState } from "react";
import { useAppAuth } from "../hooks/api/useAppAuth";
import {
  useUpdateCustomerData,
  type CustomerCredentials,
} from "../hooks/ui/customerDataStore";
import PageNotFound from "../components/pageNotFound";
import ExistingCustomerAppView from "../components/appWebview/ExistingCustomerAppView";
import {
  InsurelyFlow,
  type PrefillData,
} from "../components/appWebview/InsurelyFlow";
import { AppPoaFlow } from "../components/appWebview/poa/AppPoaFlow";
import { AppWebviewLayout } from "../components/appWebview/AppWebviewLayout";
import { LoaderView } from "../components/appWebview/LoaderView";
import appWebviewClasses from "../components/appWebview/appWebviewShared.module.css";
import { ErrorScreen } from "../components/appWebview/ErrorScreen";
import { useShouldSetAppUI } from "../hooks/ui/useShouldSetAppUI";
import {
  useEnablePoaGate,
  useSessionReplayGate,
  useVisitorPoaGate,
} from "../utils/statsig";
import {
  subscribeToUserData,
  sealUserData,
} from "../components/appWebview/userDataBridge";

export const Route = createFileRoute("/app-webview")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { flowId?: string; webview?: string } => ({
    flowId: typeof search.flowId === "string" ? search.flowId : undefined,
    // Kept in the validated search so the router preserves it across in-app navigation.
    webview: search.webview === undefined ? undefined : String(search.webview),
  }),
  component: AppWebviewPage,
  // The webview queries throw on failure, so without this they would land on the
  // mypages error page. Everything in this flow gets the app error screen instead.
  errorComponent: AppWebviewError,
});

function AppWebviewError({ reset }: ErrorComponentProps) {
  const queryErrors = useQueryErrorResetBoundary();

  return (
    <ErrorScreen
      onRetry={() => {
        // Clears the cached failures so the remount refetches instead of rethrowing.
        queryErrors.reset();
        reset();
      }}
    />
  );
}

export function AppWebviewPage() {
  const shouldSetAppUI = useShouldSetAppUI();
  const { flowId } = Route.useSearch();
  const {
    updateCredentials,
    updateName,
    updatePersonalNumber,
    updateIdentityVerificationId,
  } = useUpdateCustomerData();
  useSessionReplayGate();

  const [prefillData, setPrefillData] = useState<PrefillData | null>(null);
  const [isChatAvailable, setIsChatAvailable] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [contactError, setContactError] = useState(false);
  // Bumped on retry so the flow below remounts with fresh state.
  const [attempt, setAttempt] = useState(0);

  // Auth runs in the mode the anonymous visitor's gate picks, then sets the Statsig user
  // to its customerUuid, and the gate is read again for that user to pick the flow.
  const visitorPoa = useVisitorPoaGate();
  const poaAuth = useAppAuth(
    flowId,
    shouldSetAppUI && visitorPoa !== undefined,
    visitorPoa ?? false,
  );
  const { enabled: poaEnabled, ready: gateReady } = useEnablePoaGate(
    Boolean(poaAuth.data),
  );
  // POA mode created no lead, so a customer the gate then sends to Insurely needs one.
  const needsLead =
    gateReady &&
    !poaEnabled &&
    Boolean(poaAuth.data) &&
    !poaAuth.data?.isCustomer &&
    !poaAuth.data?.leadId;
  const leadAuth = useAppAuth(flowId, needsLead, false);
  const { data, isLoading, isError, refetch } = needsLead ? leadAuth : poaAuth;

  useEffect(() => {
    return subscribeToUserData((message) => {
      if (message.prefillData) {
        setPrefillData(message.prefillData);
        setIsChatAvailable(!!message.features?.chat);
        // The first prefill kicks off auth. Seal the bridge so no later message can overwrite it
        sealUserData();
      }
    });
  }, []);

  useEffect(() => {
    if (!data) return;

    if (data.credentials) {
      const credentials: CustomerCredentials = {
        ...data.credentials,
        expiration: new Date(data.credentials.expiration),
      };
      updateCredentials(credentials);
    }

    // Seeded on the first auth response, not in the step that needs it.
    updateName(data.firstName, data.lastName);
    updatePersonalNumber(data.personalNumber);
    updateIdentityVerificationId(data.identityVerificationId);
  }, [
    data,
    updateCredentials,
    updateName,
    updatePersonalNumber,
    updateIdentityVerificationId,
  ]);

  if (!shouldSetAppUI) return <PageNotFound />;
  // Insurely only. That flow has nothing to offer someone who already saves with us; the
  // POA flow does, because the overview is the customer's own pension data. isCustomer is
  // only "a User node exists in Neptune", which POA signup itself creates, so without the
  // poaEnabled guard every returning POA customer lands on this placeholder instead.
  // Waits for the gate too: poaEnabled is false until Statsig has loaded,
  // and this sits above the gateReady check below.
  if (gateReady && !poaEnabled && data?.isCustomer)
    return (
      <ExistingCustomerAppView
        title="Följ ditt pensionssparande via Sambla Pension"
        description="Du har ett pensionssparande hos oss. Logga in och se alla detaljer om ditt sparande på samblapension.se."
      />
    );
  // Stay on the error screen for the whole retry (TanStack flips `isError`
  // off while the refetch is in flight), and only fall through to the flow
  // once the request actually succeeds.
  if (isError || isRetrying || contactError) {
    return (
      <ErrorScreen
        isRetrying={isRetrying}
        onRetry={async () => {
          setIsRetrying(true);
          setContactError(false);
          setAttempt((count) => count + 1);
          try {
            await refetch();
          } finally {
            setIsRetrying(false);
          }
        }}
      />
    );
  }

  // Loads until auth has identified the customer and the gate has settled for them.
  // Insurely handles a lead still being created through isAuthLoading.
  if (!gateReady) {
    return (
      <AppWebviewLayout fillBody>
        <Center className={appWebviewClasses.content}>
          <LoaderView />
        </Center>
      </AppWebviewLayout>
    );
  }

  if (poaEnabled) {
    return (
      <AppPoaFlow
        key={attempt}
        prefillData={prefillData}
        isChatAvailable={isChatAvailable}
        isCustomer={Boolean(data?.isCustomer)}
      />
    );
  }

  return (
    <InsurelyFlow
      key={attempt}
      auth={data}
      prefillData={prefillData}
      isAuthLoading={isLoading}
      isChatAvailable={isChatAvailable}
      onContactError={() => setContactError(true)}
    />
  );
}
